<template>
	<v-row class="form-row align-center" no-gutters>
		<v-col cols="12" sm="3" class="field-label">{{ t("DISTANCE_UNITS") }}</v-col>
		<v-col cols="12" sm="9" class="field-content">
			<v-btn-toggle :model-value="props.modelValue" :mandatory="props.mandatory" divided rounded
				variant="outlined" density="compact" class="type-toggle" data-test="distance-unit-toggle"
				@update:model-value="onUpdate">
				<v-btn v-for="item in distanceUnitItems" :key="item.value" :value="item.value">
					{{ t(item.labelKey) }}
				</v-btn>
			</v-btn-toggle>
		</v-col>
	</v-row>
</template>

<script setup lang="ts">
import type { DistanceUnit } from "@/models/siteUnit.types";
import { useI18n } from "vue-i18n";

const props = withDefaults(
	defineProps<{
		/**
		 * 選択中の距離単位。未選択は undefined を「値として」渡す契約なので、
		 * 呼び出し側は未選択でも prop のキー自体を必ず渡すこと。
		 * `?` を付けず必須宣言のままにしているのはこのため（省略可にすると
		 * 「未選択」と「渡し忘れ」が区別できなくなる）
		 */
		modelValue: DistanceUnit | undefined;
		/**
		 * 選択の解除を禁止するか。
		 * 既定は false（点群アップロードの従来挙動 — 選択中のボタンを押すと未選択に戻る）
		 */
		mandatory?: boolean;
	}>(),
	{ mandatory: false },
);

const emit =
	defineEmits<
		(e: "update:modelValue", value: DistanceUnit | undefined) => void
	>();

const { t } = useI18n();

/**
 * 3 択の定義。distanceUnits を値 import すると spec が viewer の dist ビルドに依存するため、
 * 値はリテラルで持ち型だけを satisfies で担保する。表記は既存の i18n キーを再利用する
 */
const distanceUnitItems = [
	{ value: "METERS", labelKey: "METERS_WORD" },
	{ value: "FEET", labelKey: "FEET_WORD" },
	{ value: "US_SURVEY_FEET", labelKey: "US_SURVEY_FEET_WORD" },
] as const satisfies ReadonlyArray<{
	value: DistanceUnit;
	labelKey: string;
}>;

/** v-btn-toggle は未選択のとき undefined を渡してくるため、既知の 3 値だけを通す */
const isDistanceUnit = (value: unknown): value is DistanceUnit =>
	distanceUnitItems.some((item) => item.value === value);

const onUpdate = (value: unknown): void => {
	emit("update:modelValue", isDistanceUnit(value) ? value : undefined);
};
</script>

<style scoped>
/* 点群アップロード（addTerrainData.vue）の入力行と同じ見た目にするための最小の複製。
   親の scoped CSS が届くのは子のルート要素（.form-row）までで、内側（.field-label /
   .field-content / .type-toggle）には届かない。ルート分も親の有無に依存しないよう自前で持つ */
.form-row {
	margin-bottom: 10px;
}

.field-label {
	font-size: 14px;
	font-weight: 500;
	padding-right: 6px;
	min-height: 32px;
	display: flex;
	align-items: center;
}

.field-content {
	min-height: 32px;
	display: flex;
	align-items: center;
}

.type-toggle {
	border-radius: 24px !important;
}

:deep(.type-toggle .v-btn.v-btn--active) {
	background-color: #565664 !important;
}
</style>
